<template>
  <div>
    <layout-base :title="$t('STEP_THREE.TITLE')">
      <template slot="content">
        <bk-username class="input"
                     :username="username"
                     @change="changeUsername"
                     @clickSuggestion="clickSuggestion()"
                     :state="usernameState"
                     :suggestions="usernameSuggestions"
                     :valid="usernameValid"
                     :id="usernameId"
                     :disabled="inputDisabled"
                     :loading="usernameLoading"
                     :allow-hotjar-recording="true"
        />
        <bk-password class="input"
                     :password="password"
                     :valid="passwordValid"
                     :state="passwordState"
                     @change="changePassword"
                     :id="passwordId"
                     :disabled="inputDisabled"
        />
        <bk-email :email="email"
                  @change="changeEmail"
                  :state="emailState"
                  :valid="emailValid"
                  :id="emailId"
                  :loading="emailLoading"
                  :disabled="inputDisabled"
                  :allow-hotjar-recording="true"
        />

        <div v-if="emailNotAvailable" class="step3__email-in-use">
          <div class="step3__email-in-use__link">
            <a href="https://beta1.bildkontakte.de/de/login">{{ $t('STEP_THREE.IN_USE.LOGIN') }}</a>
          </div>
          <div class="step3__email-in-use__link">
            <a href="https://beta1.bildkontakte.de/de/send-recovery">{{ $t('STEP_THREE.IN_USE.RECOVER_PASSWORD') }}</a>
          </div>
        </div>

        <div>
          {{$t('STEP_THREE.CONDITIONS_AND_PRIVACY.1')}}
          <a href="https://www.bildkontakte.de/pops/agb.php" target="_blank">{{$t('STEP_THREE.CONDITIONS_AND_PRIVACY.2')}}</a>
          {{$t('STEP_THREE.CONDITIONS_AND_PRIVACY.3')}}
          <a href="https://www.bildkontakte.de/datenschutz" target="_blank">{{$t('STEP_THREE.CONDITIONS_AND_PRIVACY.4')}}</a>
          {{$t('STEP_THREE.CONDITIONS_AND_PRIVACY.5')}}
        </div>

        <input type="hidden" value="2" id="termsAndConditionsCheckbox">
        <input type="hidden" value="2" id="privacyNoticeCheckbox">
        <bk-checkbox id="publicProfileCheckbox"
                     class="checkbox"
                     :checked="publicProfile"
                     :valid="true"
                     @input="changePublicProfile"
        >
          {{$t('STEP_THREE.PUBLIC_PROFILE')}}
        </bk-checkbox>

        <div class="step3__error" v-if="error">
          {{$t('STEP_THREE.ERROR', {
            'errorCode': this.errorCode,
            'supportMail': $t('SUPPORT_MAIL')
          })}}
        </div>

      </template>
      <template slot="buttons">
        <bk-button @click="register" :disabled="!isFormValid">{{$t('GLOBAL.DONE')}}</bk-button>
        <bk-back-button track-action="SingleOptin" track-label="Zurueck" to="/step-two" />
      </template>
      <template slot="usp">
        <coupon-box v-if="!coupon.isEmpty()"/>
      </template>
    </layout-base>
  </div>
</template>

<script>
import LayoutBase from './LayoutBase'
import BkUsername from '../components/Username'
import BkPassword from '../components/Password'
import BkEmail from '../components/Email'
import passwordCheckMixin, { PasswordState } from '../../../mixins/passwordCheck'
import usernameCheckMixin, { UsernameState } from '../../../mixins/usernameCheck'
import triggerEvents from '../../../mixins/triggerEvent'
import emailCheckMixin, { EmailState } from '../../../mixins/emailCheck'
import { mapMutations, mapState } from 'vuex'
import BkCheckbox from '../../../common/BkCheckbox'
import BkButton from '../../../common/BkButton'
import BkBackButton from '../../../common/BkBackButton'
import debounce from 'lodash.debounce'
import { debounceTime } from '../config'
import { stepThreeGuard } from '../guards'
import CouponBox from '../components/CouponBox'

export default {
  data () {
    return {
      termsAndConditionsValid: true,
      privacyNoticeValid: true,
      usernameId: 'usernameInput',
      passwordId: 'passwordInput',
      emailId: 'emailInput',
      usernameLoading: false,
      emailLoading: false,
      error: false,
      errorCode: 0,
      inputDisabled: false
    }
  },
  components: {
    CouponBox,
    BkEmail,
    BkPassword,
    BkUsername,
    LayoutBase,
    BkCheckbox,
    BkButton,
    BkBackButton
  },
  beforeRouteEnter: stepThreeGuard,
  mixins: [passwordCheckMixin, usernameCheckMixin, emailCheckMixin, triggerEvents],
  computed: {
    ...mapState([
      'username',
      'password',
      'email',
      'birthdate',
      'geoId',
      'gender',
      'searchGender',
      'searchAgeRange',
      'termsAndConditions',
      'privacyNotice',
      'publicProfile',
      'promoter',
      'affiliateId',
      'testGroup',
      'coupon'
    ]),
    /**
     * gets an object for the api request
     * @returns {{birthday, ageTill: number, gender: *, language: (string|string), termsAndConditions: *, password: *, searchGender: *, privacyNotice: *, geoId: *, ageFrom: number, passwordRepetition: *, email: *, username: *}}
     */
    requestData () {
      const optInTAC = document.getElementById('termsAndConditionsCheckbox').value.toInt()
      const optInPN = document.getElementById('privacyNoticeCheckbox').value.toInt()

      const data = {
        'username': this.username,
        'password': this.password,
        'passwordRepetition': this.password,
        'email': this.email,
        'birthday': this.birthdate,
        'geoId': this.geoId,
        'gender': this.gender,
        'searchGender': this.searchGender,
        'ageFrom': this.searchAgeRange.min,
        'ageTill': this.searchAgeRange.max,
        'language': this.$i18n.locale.split('-')[0],
        'termsAndConditions': optInTAC,
        'privacyNotice': optInPN,
        'privateProfile': this.publicProfile ? 0 : 1,
      }

      if (this.promoter) data['promoter'] = this.promoter
      if (this.affiliateId) data['affiliateId'] = this.affiliateId
      if (this.testGroup) data['testGroup'] = this.testGroup
      if (this.coupon && this.coupon !== '') data['couponCode'] = this.coupon
      return data
    },
    isFormValid () {
      return !this.birthdate.isEmpty()
        && this.geoId !== -1
        && !this.gender.isEmpty()
        && this.usernameState === UsernameState.valid
        && (this.passwordState === PasswordState.valid || this.passwordState === PasswordState.unsafe)
        && this.emailState === EmailState.valid
    },
    emailNotAvailable () {
      return this.emailState === EmailState.notAvailable
    }
  },
  mounted () {
    if (!this.password.isEmpty()) {
      this.passwordState = this.getPasswordState(this.password, this.username);
    }
    if (!this.email.isEmpty()) {
      this.changeEmail(this.email);
    }
  },
  methods: {
    ...mapMutations([
      'updateEmail',
      'updatePassword',
      'updateUsername',
      'updateTermsAndConditions',
      'updatePrivacyNotice',
      'updatePublicProfile',
      'updateAccessToken',
      'registrationFinished'
    ]),
    /**
     * register to the user if it was successful the user login
     */
    register () {
      if (!this.isFormValid) {
        this.checkCheckboxes()
        return
      }

      this.inputDisabled = true
      this.requestRegister(this.requestData)
        .then((data) => {
          this.inputDisabled = false

          if (this.publicProfile) {
            this.fireTrackEvent('SingleOptin', 'NichtMitgliederAngehakt')
          }

          this.updateAccessToken(data.access_token)
          this.$router.push('email-validation')

          this.registrationFinished()
          this.fireDimensionEvent();
          this.fireTrackEvent('SingleOptin', 'Abgeschlossen')
        })
        .catch(error => {
          this.inputDisabled = false
          if( error.body.data && error.body.data.formErrors ){
            this.handleFormErrors(error.body.data.formErrors);
          } else {
            this.error = true
          }
          if (typeof error === 'number') {
            this.errorCode = error
          } else {
            this.errorCode = error.status
          }

          this.fireTrackEvent('Error', 'Klick: Fertig - Fehlermeldung: Ein Fehler ist aufgetreten', `Status: ${this.errorCode} | body: ${error.bodyText}`);
          this.$sentry.captureException(error)
        })
    },
    /**
     * Check if the checkboxes are checked
     * If not the checkboxes set to invalid
     */
    checkCheckboxes () {
      if (!this.termsAndConditions) this.termsAndConditionsValid = false
      if (!this.privacyNotice) this.privacyNoticeValid = false
    },
    /***
     * Change and validate the password
     * @param password
     */
    changePassword (password) {
      this.error = false
      this.updatePassword(password)
      this.passwordState = this.getPasswordState(password, this.username)
    },
    /**
     * Check if the username is valid
     * @param username
     */
    validateUsername: debounce(function (username) {
      this.usernameLoading = true
      this.checkUsername(username).then(() => {
        this.usernameLoading = false
      })
    }, debounceTime),
    /**
     * Change and validate the username
     * @param username
     */
    changeUsername (username) {
      this.error = false
      this.updateUsername(username)
      this.validateUsername(username)
    },
    /**
     * Change the value of the terms of trade checkbox
     * @param value
     */
    changeTermsOfTrade (value) {
      this.termsAndConditionsValid = true
      this.updateTermsAndConditions(value)
    },
    /**
     * Change the value of the privacy notice
     * @param value
     */
    changePrivacyNotice (value) {
      this.privacyNoticeValid = true
      this.updatePrivacyNotice(value)
    },
    changePublicProfile (value) {
      this.updatePublicProfile(value)
    },
    /**
     * Change and validate the email
     * @param email
     */
    changeEmail (email) {
      this.error = false
      this.updateEmail(email)
      this.emailValidate(email)
    },
    /**
     * Validate the email
     * @param email
     */
    emailValidate: debounce(function (email) {
      this.emailLoading = true
      this.validateEmailAndCheckExisting(email).finally(()=> {this.emailLoading = false});
    }, debounceTime),
    clickSuggestion () {
      this.fireTrackEvent('SingleOptin', 'SpitznamenvorschlagAusgewaehlt')
    },
    fireDimensionEvent()
    {
      let dimEvent = {
        betaRegistrierte: 'bkutg0002'
      };

      this.$dataLayer.push(dimEvent);
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "../../../scss/colors";

  .input {
    margin-bottom: 16px;
  }

  .step3 {
    &__email-in-use {
      margin-bottom: 16px;

      &__link {
        font-size: 12px;
        line-height: 32px;
      }
    }

    &__error {
      margin: 24px 0 0;
      padding: 8px;
      border: 2px $error solid;
    }
  }

  .checkbox {
    margin-top: 16px;
  }
</style>

